import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom icon for user location (blue)
const userIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom icon for parishes (purple to match app theme)
const parishIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to fit map bounds to markers
function FitBounds({ bounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      const leafletBounds = L.latLngBounds(bounds);
      map.fitBounds(leafletBounds, { padding: [50, 50] });
    }
  }, [map, bounds]);
  
  return null;
}

export default function SacramentFinderMap({ userLocation, results, locationAddress }) {
  const navigate = useNavigate();
  
  // Get unique parishes from results (avoid duplicate markers)
  const uniqueParishes = [];
  const seenParishIds = new Set();
  
  for (const result of results) {
    if (result.coordinates && !seenParishIds.has(result.parish_id)) {
      seenParishIds.add(result.parish_id);
      uniqueParishes.push({
        parish_id: result.parish_id,
        parish_name: result.parish_name,
        coordinates: result.coordinates,
        distance_formatted: result.distance_formatted,
        address: result.address
      });
    }
  }
  
  // Calculate bounds to fit all markers
  const bounds = [];
  if (userLocation) {
    bounds.push([userLocation.lat, userLocation.lng]);
  }
  uniqueParishes.forEach(p => {
    if (p.coordinates?.lat && p.coordinates?.lng) {
      bounds.push([p.coordinates.lat, p.coordinates.lng]);
    }
  });
  
  // Default center (Memphis, TN area) if no location
  const defaultCenter = userLocation 
    ? [userLocation.lat, userLocation.lng] 
    : [35.1495, -90.0490];
  
  if (bounds.length === 0) {
    return null; // Don't show map if no markers
  }
  
  return (
    <div className="w-full h-[400px] rounded-lg overflow-hidden border border-[#E5E0D5] mb-6" data-testid="sacrament-finder-map">
      <MapContainer
        center={defaultCenter}
        zoom={10}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <FitBounds bounds={bounds} />
        
        {/* User location marker */}
        {userLocation && (
          <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
            <Popup>
              <div className="text-sm">
                <strong>Your Search Location</strong>
                {locationAddress && <p className="text-gray-600 mt-1">{locationAddress}</p>}
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Parish markers */}
        {uniqueParishes.map((parish) => (
          <Marker
            key={parish.parish_id}
            position={[parish.coordinates.lat, parish.coordinates.lng]}
            icon={parishIcon}
            eventHandlers={{
              click: () => {
                // Don't navigate on marker click, use popup button instead
              }
            }}
          >
            <Popup>
              <div className="text-sm min-w-[200px]">
                <strong className="text-[#6B4C3B]">{parish.parish_name}</strong>
                {parish.distance_formatted && (
                  <p className="text-gray-600 mt-1">{parish.distance_formatted} away</p>
                )}
                {parish.address && (
                  <p className="text-gray-500 text-xs mt-1">{parish.address}</p>
                )}
                <button
                  onClick={() => navigate(`/parish/${parish.parish_id}`)}
                  className="mt-2 px-3 py-1 bg-[var(--liturgical-primary)] text-white text-xs rounded hover:brightness-90 transition-colors"
                >
                  View Parish
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
